import { useAccountStore } from '@/stores/account'
import { Env, IS_INTL } from '@/constants/global'
import i18n from '@/i18n'
import { I18nKeys } from '@/constants/i18n'
import CryptoJS from 'crypto-js'

// @ts-ignore
// const t = i18n.global.t

export const isDev = import.meta.env.VITE_ENV === Env.DEV

export function sleep(time: number): Promise<void> {
  return new Promise<void>((resolve) => {
    setTimeout(() => {
      resolve()
    }, time)
  })
}

export function keyValueStr2Object(str: string): Record<string, any> {
  const obj: Record<string, any> = {}

  str.split(', ').forEach((item) => {
    const keyVal = item.split(': ')

    // if value contains an object structure
    if (keyVal[1].includes('{') && keyVal[1].includes('}')) {
      obj[keyVal[0]] = JSON.parse(keyVal[1])
    } else {
      obj[keyVal[0]] = keyVal[1]
    }
  })

  return obj
}

export function clearData() {
  const accountStore = useAccountStore()
  accountStore.setToken('')
  localStorage.removeItem('image')
  localStorage.removeItem('account')
  localStorage.removeItem('global')
}

export function isSupportBrowser() {
  const userAgent = navigator.userAgent
  const isIPadDevice = isIPad()
  
  const isChrome = isIPadDevice 
    ? userAgent.indexOf('CriOS') > -1  // iPad Chrome
    : userAgent.indexOf('Chrome') > -1  // 桌面 Chrome
  
  const isSafari = userAgent.indexOf('Safari') > -1 && 
    userAgent.indexOf('Version/') > -1 && 
    (!isChrome || isIPadDevice)  // iPad 上 Safari 和 Chrome 都可以

  if (isIPadDevice) {
    return isSafari || isChrome
  }

  return isChrome || isSafari
}

export function isElectron() {
  // @ts-ignore
  return !!window.electronAPI
}

export function detectOS() {
  const userAgent = navigator.userAgent.toLowerCase()

  if (userAgent.includes('windows')) {
    return 'Windows'
  } else if (userAgent.includes('macintosh') || userAgent.includes('mac os')) {
    return 'Mac'
  } else {
    return 'Unknown'
  }
}

function loadTextFileContent(url: string): Promise<string> {
  return new Promise((resolve, reject) => {
    fetch(url)
      .then((response) => resolve(response.text())) // 将响应体转换为文本
      .catch((error) => {
        reject(error)
      })
  })
}

function getLatestAppFileName(ymlText: string): string {
  const platform = detectOS()
  if (platform === 'Windows') {
    const windowsUrlMatch = ymlText.match(/url: (.+\.exe)/)
    return windowsUrlMatch ? windowsUrlMatch[1] : ''
  } else if (platform === 'Mac') {
    const macUrls = [...ymlText.matchAll(/url: (.+)/g)]
    return macUrls && macUrls.length > 1 ? macUrls[1][1] : ''
  }
  return ''
}

export async function downloadFile(url: string, fileName: string) {
  // 创建一个临时的<a>标签用于下载文件
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  document.body.appendChild(a)
  a.click()

  // 移除<a>标签并释放Blob URL
  document.body.removeChild(a)
}

export async function downloadApp() {
  const baseURL =
    import.meta.env.VITE_ENV === 'prod'
      ? IS_INTL ? 'https://resource.lookfashion.ai/apps' : 'https://resource.look-ai.cn/apps'
      : IS_INTL ? 'https://resource.lookfashion.ai/apps/develop' : 'http://static.base.com/app-electron'
  const latestFileName = detectOS() === 'Windows' ? 'latest.yml' : 'latest-mac.yml'
  const ymlContent = await loadTextFileContent(
    `${baseURL}/${latestFileName}?time=${new Date().getTime()}`
  )
  console.info('版本描述文件')
  console.info(ymlContent)
  const appFileName = getLatestAppFileName(ymlContent)
  if (appFileName) {
    const downloadURL = `${baseURL}/${appFileName}`
    console.info('最新APP下载地址', downloadURL)
    await downloadFile(downloadURL, appFileName)
  }
}

export async function copyText(text: string) {
  // @ts-ignore
  const t = i18n.global.t

  try {
    await navigator.clipboard.writeText(text);
    (window as any).$message.success(t(I18nKeys.system.copySuccess))
  } catch (err) {
    (window as any).$message.error(`${t(I18nKeys.system.copySuccess)}${err}`)
  }
}

/**
 * 根据给定的格式来格式化日期
 * @param timestamp 时间戳
 * @param format 格式化字符串
 * @returns 格式化后的日期
 */
export function formatDate(timestamp: number, format: string): string {
  const date = new Date(timestamp)
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: userTimeZone
  }
  const formattedDate = new Intl.DateTimeFormat('default', options).format(date)
  const [month, day, year] = formattedDate.split('/')

  return format
    .replace('yyyy', year)
    .replace('MM', month)
    .replace('dd', day)
}

/**
 * 根据时区格式化时间，格式会自动匹配给定的语言环境输出
 * @param timestamp 时间戳
 * @param lang 语言
 * @param showTime 是否显示时间
 * @returns 格式化后的时间
 */
export const formatTime = (timestamp: number | string, lang: string, showTime = true) => {
  const availableTime = new Date(timestamp)
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: userTimeZone,
    hour12: false
  }

  if (showTime) {
    options.hour = '2-digit'
    options.minute = '2-digit'
    options.second = '2-digit'
  }

  return new Intl.DateTimeFormat(lang, options).format(availableTime)
}

export function getBaseURL() {
  let baseURL: string
  if (import.meta.env.DEV) {
    baseURL = '/v2'
  } else {
    baseURL = import.meta.env.VITE_BASE_URL_V2
  }
  return baseURL
}

export function generateHash(data: any): string {
  // 将对象转换为规范化的字符串
  const str = Object.entries(data)
    .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
    .map(([key, value]) => {
      if (typeof value === 'object' && value !== null) {
        return `${key}:${generateHash(value)}`
      }
      return `${key}:${value}`
    })
    .join('|')

  // 使用简单的哈希算法
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash // Convert to 32bit integer
  }
  return Math.abs(hash).toString(36)
}

// 哈希处理函数，将字符串转换为SHA-256哈希值
export function hashString(str: string): string {
  if (!str) return '';
  
  return CryptoJS.SHA256(str).toString()
}

export function checkMobile() {
  const ua = navigator.userAgent.toLowerCase()

  // 更新移动设备检测逻辑

  return (
    /iphone|ipad|ipod|android|crios|fxios|mobile|tablet/.test(ua) ||
    // iPad on iOS 13 detection
    (ua.includes('macintosh') && 'ontouchend' in document)
  )
}

export function openBrowserPage(url: string) {
  if (checkMobile()) {
    location.href = url
  } else {
    const link = document.createElement('a')
    link.href = url
    link.target = '_blank'
    link.rel = 'noopener noreferrer' // 添加安全属性
    link.click()
  }
}

export function isIPad() {
  const ua = navigator.userAgent.toLowerCase()
  
  // 检测是否是 iPad
  const isIpad = /ipad/.test(ua)
  
  // 检测是否是运行在 iPad 上的 Safari
  const isMacOS = /macintosh/.test(ua)
  const isTouch = 'ontouchend' in document
  const isIpadOS = isMacOS && isTouch
  
  // 检测屏幕尺寸是否符合 iPad
  const isTabletSize = window.innerWidth >= 768 && window.innerWidth <= 1024
  
  return isIpad || isIpadOS || isTabletSize
}
